import React, {Component} from 'react'
import Lightbox from 'react-image-lightbox';
import './App.css'
import AlertMessage from './AlertMessage';
import FieldLevelHelp from './components/FieldLevelHelp';
import withRequisitionStep from './components/withRequisitionStep';
import withWorkflow from './components/withWorkflow';
import withPricing from './components/withPricing';
import { scrollToMessage } from './util/scrollToMessage';
import RequiredFieldDisclaimer from './components/RequiredFieldDisclaimer';
import { confirmPriceIncrease, skipStep } from './util/apiUtils';
import AccessionedOrderAlert from './components/AccessionedOrderAlert';
import MedicareDiscardAlert from './components/MedicareDiscardAlert';
import PriceDescription from './payment/PriceDescription';
import {getBillingTypeDisplayName, isClientInFlOrNy, formatCurrency, isUKOrder, getCustomerCareNumber} from './util/gearOrderUtil';
import BillingFaqLink from './components/BillingFaqLink';
import Modal from './components/Modal';

/**
 * Component for the 'billing' step of the GEAR order workflow.
 */
class BillingOptions extends Component {
	constructor(props) {
		super(props);
		
		let billingMethod;
		let subOption;
		if( this.props.activeGearRequisitionId ) {
			let activeGearRequisition = this.props.getRequisitionFromOrder( this.props.gearOrder, this.props.activeGearRequisitionId );
			billingMethod = activeGearRequisition.billingMethod;
			if(isUKOrder(this.props.gearOrder) && billingMethod && billingMethod.startsWith(process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE) 
				&& billingMethod.includes(":")) {
				let billingMethodSplits = billingMethod.split(":");
				billingMethod = billingMethodSplits[0];
				subOption = billingMethodSplits[1];
			}
		}		
		let isPrcUser = this.props.userInfo.type === 'prc';
		let covidRegistration = this.props.activeGearRequisition?.covidRegistration || this.props.activeGearRequisition?.omsOrderableItemId === 902;

		this.skipBillingOptionName = 'Let my patient select their billing option';
		this.uninsuredBillingOptionName = 'Uninsured';
		this.uninsuredHRSABillingOptionName = 'Uninsured (HRSA Elligible)';
	
		let defaultBillingOptions = getSupportedBillingMethodInputFields({
			practitioner: this.props.gearOrder?.practitioner,
			orderableItem: this.props.omsRequisition?.orderableItem,
			userType: this.props.userInfo.type,
			savedBillingMethod: billingMethod && billingMethod + (subOption ? `:${subOption}` : '')
		});
		let skipBillingOption = [];
		let uninsuredBillingOption = [];
		let uninsuredHRSABillingOption = [];
		let medicaredBillingOption = [];
		
		// Create the "skip" and uninsured billing options if valid (Don't show for PRC Covid Registration)
		if (!isUKOrder(this.props.gearOrder) && !(isPrcUser && covidRegistration)) {
			//Only show Unknown option for Clients for dropship orders
			if (!billingMethod && this.props.gearOrder.containsDropShipRequisition && !isPrcUser) {
				skipBillingOption.push({
					name: this.skipBillingOptionName,
					description: 'Pricing estimation and online payments will only be available once your patient chooses a billing option.'
				});
	
				// Default to 'Unknown' for myGDX dropships
				if (!isPrcUser) {
					billingMethod = this.skipBillingOptionName;
				}
			}

			// Create the "Uninsured" billing options if it is COVID registration
			if (covidRegistration) {
				uninsuredBillingOption.push({
					name: this.uninsuredBillingOptionName,
					description: 'This patient may have active insurance coverage and is not eligible for HRSA reimbursement.'
				});
				
				uninsuredHRSABillingOption.push({
					name: this.uninsuredHRSABillingOptionName,
					description: "This patient does not have active insurance coverage. <br/> <strong>A SSN or Driver's License number is required for this billing option.</strong>"
				});
			}
		}

		// SCR-7005: Show "Uninsured (HRSA Elligible)" billing option and remove Physician Account option when patients of client A9731 are registering Covid tests
		if (covidRegistration && isPrcUser && this.props.gearOrder?.practitioner?.gsdlNo === 'A9731') {			
			uninsuredHRSABillingOption.push({
				name: this.uninsuredHRSABillingOptionName,
				description: "This patient does not have active insurance coverage. <br/> <strong>A SSN or Driver's License number is required for this billing option.</strong>"
			});

			defaultBillingOptions = defaultBillingOptions.filter(option => option.name !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE);
		}

		if (this.isMedicareSupported(this.props.gearOrder)) {
			medicaredBillingOption.push({
				name: process.env.REACT_APP_BILLING_MEDICARE_VALUE,
				description: 'Medicare Advantage Plans use the Bill Insurance option below.'
			});
		}

		// Check if the page should be 'locked' (Requisition has been accessioned)
		let orderReceived = Boolean(this.props.activeGearRequisition && this.props.activeGearRequisition.accessionNo);
		let disableInputs = this.props.userInfo.adminUser !== 'true' && orderReceived;

		if (isUKOrder(this.props.gearOrder) && !billingMethod) {
			//Select default billing method for UK orders if not already selected
			({ billingMethod, subOption } = getDefaultUkBillingOption({
				practitioner: this.props.gearOrder.practitioner,
				availableBillingOptions: defaultBillingOptions
			}));
			if (billingMethod === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE) {
				disableInputs = true;
			}
		}

		this.state = {
			billingOptions: [...skipBillingOption, ...uninsuredBillingOption, ...uninsuredHRSABillingOption, ...medicaredBillingOption, ...defaultBillingOptions],
			requisitionPriceObject: {},
			fetchingPricesCount: 0,
			disableInputs: disableInputs,
			form: {
				billingMethod: {
					value: billingMethod,
					subOption: subOption ?? null
				}
			},
			isPrcUser: isPrcUser,
			showBillingOptionsImage: false,
			covidRegistration: covidRegistration,
			dirty: false,
			orderReceived: orderReceived
		}

		this.changeBilling = this.changeBilling.bind(this);
		this.goBack = this.goBack.bind(this);
		this.validate = this.validate.bind(this);
		this.onClick = this.onClick.bind(this);
		this.loadPrices = this.loadPrices.bind(this);
    }

	/**
	 * When the component mounts get the prices if none have been fetched and they are not actively being fetched.
	 */
	componentDidMount() {
		// Manual ref management because of the limitations of class components
		if (this.props.editRef) {
			this.props.editRef({
				save: this.onSave,
				reset: () => {},
				isDirty: this.isDirty,
			});
		}

		// TODO: Should prices be hoisted up to prevent re-fetching when going back to Billing Options step?
		if (Object.keys(this.state.requisitionPriceObject).length === 0 && this.state.fetchingPricesCount === 0) {
			// Don't get prices when practitioner has "Do Not Quote Prices to Patients" attribute.
			if (!this.props.doNotQuotePrices) {
				this.loadPrices();
			}
		}
	}

	/**
	 * Load the prices for each billing method.
	 */
	loadPrices() {
		// clear existing prices
		this.setState({
			requisitionPriceObject: {}
		})

		// Loop through each billing method and fetch the price and description
		this.state.billingOptions.forEach(billingMethod => {
			// do not get the price for physician account, it would return $0 and we will not display it
			// Also, don't try to fetch price for the "skip" option
			let fetchPrice = (billingMethod.name !== this.skipBillingOptionName && billingMethod.name !== this.uninsuredBillingOption && billingMethod.name !== this.uninsuredHRSABillingOption);
			if (billingMethod.name.indexOf("Physician") === -1 && fetchPrice) {
				this.setState(prevState => ({
					fetchingPricesCount: prevState.fetchingPricesCount+1,
				}));
				this.props.getRequisitionPrice( this.props.activeGearRequisitionId, billingMethod.name )
				.then(requisitionPrice => {
					let tempRequisitionPriceObject = this.state.requisitionPriceObject;
					tempRequisitionPriceObject[billingMethod.name] = requisitionPrice;
					this.setState( prevState => ({
						requisitionPriceObject: tempRequisitionPriceObject,
						fetchingPricesCount: prevState.fetchingPricesCount-1
					}));
				})
				.catch(error => {
					let tempRequisitionPriceObject = this.state.requisitionPriceObject;
					tempRequisitionPriceObject[billingMethod.name] = {
						gearRequisitionId: this.props.activeGearRequisitionId,
						billingMethod: billingMethod.name,
						price: null,
						placeholder: false,
						error: true
					};
					let errorMsg = `We are having difficulty retrieving your price. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`;
					if (!this.state.isPrcUser) {
						errorMsg = errorMsg + ' or consult our Fee Schedule';
					}
					errorMsg = errorMsg + '.';
					this.setState(prevState => ({
						requisitionPriceObject: tempRequisitionPriceObject,
						fetchingPricesCount: prevState.fetchingPricesCount-1,
						errorMsg: errorMsg,
						showError: true
					}));
				});
			}
		});
	}

	/**
	 * Medicare is supported if:
	 *  - The user is 'prc'
	 *  - The practitioner supports the Medicare billing option
	 *  - The requisition supports the Medicare billing option
	 *  - The patient is 45+
	 */
	isMedicareSupported = (gearOrder) => {
		// SCR-6136: Only show the Medicare option for patient registration
		let isPrcUser = this.props.userInfo.type === 'prc';

        let tricareOnlyTests = (process.env.REACT_APP_STATUTORILY_NON_COVERED_MEDICARE_ITEMS || '').split(',').map(Number);
        let medicareTricareEligible = this.props.omsRequisition.orderableItem.mcrEligible || tricareOnlyTests.indexOf(this.props.omsRequisition.orderableItem.id) > -1;
		// SCR-6197: Include Medicare for patient registration if the practitioner supports Medicare (Don't base this off the req configuration)
		// SCR-6787: Only show Medicare choice for self-authorizing clients

		return (
			isPrcUser &&
			gearOrder &&
			gearOrder.practitioner &&
			gearOrder.practitioner.billingMethods &&
			gearOrder.practitioner.billingMethods.includes('Medicare') &&
			this.props.omsRequisition &&
			this.props.omsRequisition.orderableItem &&
			medicareTricareEligible &&
			gearOrder.practitioner.gsdlNo === gearOrder.practitioner.authorizingDoctorGsdlNo && 
			!isUKOrder(this.props.gearOrder)
		);
	}
	getPriceRange(billingOptionName, billingSubOptionName) {
		const isukOrder = isUKOrder(this.props.gearOrder);
		let billingOption = this.state.requisitionPriceObject[billingOptionName];
		let priceRange = null;
		if (billingOption) {
			if (billingOption.error) {
				priceRange = null;
			} else if (billingOptionName === "Medicare or Tricare") {
				if (billingOption.medicarePrice === billingOption.tricarePrice) {
					priceRange = formatCurrency(billingOption.medicarePrice, isukOrder); 
				} else {
					let min = Math.min(billingOption.medicarePrice, billingOption.tricarePrice);
					let max = Math.max(billingOption.medicarePrice, billingOption.tricarePrice);
					priceRange = formatCurrency(min, isukOrder) + " - " + formatCurrency(max, isukOrder);
				}
			} else if (billingOptionName.indexOf("No Insurance") !== -1) {
				priceRange = formatCurrency(billingOption.price, isukOrder);
			} else if (billingOptionName.indexOf("Insurance") !== -1) {
				if (this.state.covidRegistration) {
					priceRange = formatCurrency("0.00", isukOrder);
				} else if (!isClientInFlOrNy(this.props.gearOrder) && billingOption.maxOutOfPocket && billingOption.maxOutOfPocket > billingOption.price) {
					const additionalCost = billingOption.maxOutOfPocket - billingOption.price;
					priceRange = (
						<>
							<h6 className="list-group-item-heading mb-0" style={{ fontSize: '14px' }}>Initial Payment Today</h6>
							<span>{formatCurrency(billingOption.price, isukOrder)}</span>
							<h6 className="list-group-item-heading mb-0 mt-2" style={{ fontSize: '14px' }}>Additional After Insurance</h6>
							<span>{formatCurrency("0.00", isukOrder)} - {formatCurrency(additionalCost, isukOrder)}</span>
						</>
					);
				} else {
					priceRange = (
						<>
							{!isukOrder && <h6 className="list-group-item-heading mb-0" style={{ fontSize: '14px' }}>Initial Payment Today</h6>}
							<span>{formatCurrency(billingOption.price, isukOrder)}</span>
						</>
					);
				}
			} else if (billingOptionName === process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE && billingSubOptionName) {
				let subOptionPrice = billingOption.subOptionsPriceMap[billingSubOptionName];
				priceRange = formatCurrency(subOptionPrice, isukOrder);
			}
		}

		if (priceRange === null) {
			return <FieldLevelHelp textClassName="text-warning" iconClassName="fa-exclamation-triangle" helpText='Unable to determine price.' large="true" />;
		}
		
		return priceRange;
	}
	setDirty = (value) => {
		this.setState({dirty: value});
		this.props.setDirty(value);
	}
	isDirty = () => {
		return this.state.dirty;
	}
	 goBack(e){
		  if (this.props.doConfirm(e)){
				this.props.updateStep(this.props.gearOrder, 'billing', 'Back', this.props.activeGearRequisitionId);
		  } else {
				e.preventDefault();
		  }
	 }
	changeBilling(e) {
		if(e != null &&  e.target != null) {
            this.setDirty(true);
			let form = Object.assign({}, this.state.form);
			const value = e.currentTarget.value;
			form.billingMethod.value = value;

			const billingOption = this.state.billingOptions.find(option => option.name === value);
			if (billingOption?.subBillingOptions?.subOptions?.length === 1) {
				form.billingMethod.subOption = billingOption.subBillingOptions.subOptions[0];
			}

			this.setState({
				form: this.validate(form)
			});
		}
	}
	validate(form) {
		Object.values(form).forEach(prop => {
			prop.valid = true;
			prop.message = "";
		});
		if (!this.state.form.billingMethod.value) {
			form.billingMethod.valid = false;
			form.billingMethod.message = "Please select a billing option.";
		}
		
		return form;
	}

	// This onSave works separately from the onClick for the inlineEdit functions
	onSave = () => {
		if (this.state.form.billingMethod.value === this.skipBillingOptionName) {
			const step = this.props.getWorkflowStep('billing');
			this.props.setLoading(true);
			return skipStep(this.props.gearOrder.id, step.id, this.props.activeGearRequisitionId)
			.then(json => {
				if (json.id) {
					this.props.setGearOrder(json);
					this.setDirty(false);
					this.props.updateStep(this.props.gearOrder, 'billing', 'Next', this.props.activeGearRequisitionId);
					return Promise.resolve(json);
				} else if (json.errors) {
					this.setState({
						showError: true,
						errorMessage: json.errors[0].field + ' ' + json.errors[0].defaultMessage
					});
					return Promise.reject(`an unexpected error has occurred. please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`);
				} else {
					this.setState({
						showError: true,
						errorMsg: `An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`
					});
					return Promise.reject(`An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`);
				}
			})
			.catch(e => {
				let errorMessage = e.message;
				if (!errorMessage) {
					errorMessage = `An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`;
				}
				this.setState({
					showError: true,
					errorMsg: errorMessage
				});
				return Promise.reject(`An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`);
			})
			.finally( () => this.props.setLoading(false));
		} else {
			let validatedForm = this.validate(this.state.form);
			let isValid = Object.values(validatedForm).every(prop => prop.valid);
			let billingMethodName = validatedForm.billingMethod.value;
			
			if(isUKOrder(this.props.gearOrder)) {
				validatedForm = this.validateUKBillingOptions(this.state.form);
				isValid = Object.values(validatedForm).every(prop => prop.valid);
				if(isValid && validatedForm.billingMethod.value === process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE) {
					billingMethodName = billingMethodName.concat(':').concat(validatedForm.billingMethod.subOption);
				}
			}

			this.setState({
				form: validatedForm
			}, () => {
				if (!isValid) {
					scrollToMessage();
				}
			});

			if (isValid) {
				if (this.shouldWarnOfPriceIncrease()) {
					this.setState({
						showConfirmPriceIncrease: true,
						isConfirmedPriceIncrease: false
				});
					return Promise.reject('ValidationError');
				}

				this.props.setLoading(true);
				return fetch(process.env.REACT_APP_GEAR_REST_URL+'/billingMethod/', {
					method: 'PUT',
					mode: 'cors',
					credentials: 'include',
					timeout:1000,
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						'billingMethod': billingMethodName,
						'gearReqId': this.props.activeGearRequisitionId,
					})
				})
				.then(response => {
					if (response.ok) {
						return response.json();
					} else {
						return Promise.reject(`An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`);
					}
				})
				.then(json => {
					if (json.id) {
						if (this.state.isConfirmedPriceIncrease) {
							// Trigger patient email
							confirmPriceIncrease(json.id, this.props.activeGearRequisitionId, 'BillingOptions');
						}
						this.props.setGearOrder( json );
						this.setDirty(false);
						this.props.updateStep(json, 'billing', 'Next', this.props.activeGearRequisitionId);
						return Promise.resolve(json);
					}
				})
				.catch(error => {
					this.props.showError(error.message);
					console.log('Billing option PUT request failed', error);
					if (this.props.editRef) {
						return Promise.reject(`An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props.gearOrder)}.`);
					}
				})
				.finally( () => this.props.setLoading(false) );
			} else {
				if (this.props.editRef) {
					return Promise.reject('ValidationError');
				}
			}
		}
	}

	onClick(event) {
		event?.preventDefault && event.preventDefault();
		//When Online Order option is preselected for UK order, other billing options needs to be locked but it will need to save that selection.
		if (this.state.orderReceived || (this.state.disableInputs && !this.state.form.billingMethod.value === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE)) {
			// No back-end updates required
			this.props.updateStep(this.props.gearOrder, 'billing', 'Next', this.props.activeGearRequisitionId);
		} else {
			this.onSave();
		}
	}

	showBillingOptionsImage = (e) => {
		e.preventDefault();
		this.setState({ showBillingOptionsImage: true });
	}

	/**
	 * SCR-7971
	 * Checks if the patient has completed the payment step and the billing method is changed in a way that might 
	 * increase the price.
	 * 
	 * When switching between Insurance and No Insurance we will compare the "Due Today" amount to determine if a
	 * price increase warning is needed.
	 * 
	 * Price increase warning scenarios
	 * Any -> Client Bill - No price increase
	 * No Billing Method -> Any except Client Bill - Price increase
	 * Client -> Medicare - Price increase if Medicare price > $0
	 * Client -> Cash or Insurance - Price increase
	 * Medicare -> Cash or Insurance - Price increase
	 * Cash <-> Insurance - Price increase if "Due Today" increases
	 */
	shouldWarnOfPriceIncrease = () => {
		const savedBillingMethod = this.props.activeGearRequisition?.billingMethod;
		const selectedBillingMethod = this.state.form.billingMethod?.value;

		if (savedBillingMethod === selectedBillingMethod || this.state.isConfirmedPriceIncrease) {
			return false;
		}

		/*
		 * Some workflows will not save paymentInfo (Medicare $0 or Client Bill). We still need to warn in this case
		 * if the patient has confirmed the order.
		 * If a payment was saved, make sure it came from the patient.
		 */
		const hasPayment = !!this.props.activeGearRequisition?.paymentInfo?.id;
		const patientPayment = this.props.activeGearRequisition?.paymentInfo?.paymentFrom === 'prc';

		if (
			this.props.userInfo.type === 'myGDX' && // Only warn when a client changes the billing method
			this.props.gearOrder?.workflow?.patientConfirmed && // Patient has completed the confirmation step
			(!hasPayment || patientPayment) // If a payment was saved, it came from the patient
		) {
			// Any -> Client Bill - No price increase
			if (selectedBillingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE) {
				return false;
			}

			// Client -> Medicare - Price increase if Medicare price > $0
			if (
				savedBillingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE &&
				selectedBillingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE
			) {
				let medicarePrice =
					this.state.requisitionPriceObject?.[process.env.REACT_APP_BILLING_MEDICARE_VALUE]?.price;
				return !!medicarePrice;
			}

			// Medicare -> Cash or Insurance - Price increase
			// Client -> Cash or Insurance - Price increase
			if (
				(savedBillingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE ||
					savedBillingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE) &&
				(selectedBillingMethod === process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE ||
					selectedBillingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE)
			) {
				return true;
			}

			// Changing between Cash and Insurance
			if (
				(savedBillingMethod === process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE ||
					savedBillingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE) &&
				(selectedBillingMethod === process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE ||
					selectedBillingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE)
			) {
				const savedPrice = this.state.requisitionPriceObject?.[savedBillingMethod]?.price;
				const selectedPrice = this.state.requisitionPriceObject?.[selectedBillingMethod]?.price;

				// Warn if either price is not available or if the new price is greater than the saved price
				return !selectedPrice || !savedPrice || (selectedPrice > savedPrice);
			}

			// No Billing Method -> Any except Client Bill
			if (!savedBillingMethod && selectedBillingMethod !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE) {
				const selectedPrice = this.state.requisitionPriceObject?.[selectedBillingMethod]?.price;
				// True if price is not available or if price is not $0
				return selectedPrice !== 0;
			}
		}
		return false;
	}

	/**
	 * Handle the 'Yes' choice from the Price Increase Confirmation dialog.
	 * Flags that we have handled the price increase confirmation and attempts to submit the billing method.
	 */
	confirmPriceIncrease = () => {
		this.setState({
			showConfirmPriceIncrease: false,
			isConfirmedPriceIncrease: true
		}, () => {
			this.onClick();
		});
	}

	/**
	 * Handle the 'No' choice from the Price Increase Confirmation dialog.
	 * Close the confirmation modal and reset to the original billing method
	 */
	denyPriceIncrease = () => {
		this.setState({
			showConfirmPriceIncrease: false,
			isConfirmedPriceIncrease: false,
			form: {
				billingMethod: {
					value: this.props.activeGearRequisition?.billingMethod
				}
			},
		});
	}

	closePriceIncreaseConfirmation = () => {
		this.setState({
			showConfirmPriceIncrease: false
		});
	}

	handleSubOption = (event) => {
		const selectedSubOption = event.currentTarget.value;
		let form = Object.assign({}, this.state.form);
		form.billingMethod.subOption = selectedSubOption;
		this.setState({
			form: form
		});
	};

  validateUKBillingOptions(form) {
		Object.values(form).forEach(prop => {
			prop.valid = true;
			prop.message = "";
		});
		if (!this.state.form.billingMethod.value) {
			form.billingMethod.valid = false;
			form.billingMethod.message = "Please select a billing option.";
		}
		if(this.state.form.billingMethod.value === process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE && !form.billingMethod.subOption) {
			form.billingMethod.valid = false;
			form.billingMethod.message = "Please select a Patient Pay Wholesale or Retail price option.";
		}		
		return form;
	}
 

	render() {
		var options = this.state.billingOptions;
		const isPrcUser = this.state.isPrcUser;
		const simplifiedClientBilling = this.props.gearOrder.simplifiedClientBilling;

		const showOnlineOrderNumberOption = this.state.form.billingMethod.value === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE && 
								options.some(option => option.name === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE);

		// SCR-6450: For 'prc' users when not patient-first registration, don't include Client unless it is the currently selected option
		if (
			options.length > 1 &&
			isPrcUser &&
			!this.props.gearOrder.patientRegistration &&
			this.props.activeGearRequisition &&
			this.props.activeGearRequisition.billingMethod !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE
		) {
			options = options.filter((option) => option.name !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE);
		}

		return (
				<div>
					{!this.props.editRef && <h2>Billing Options<br />
						<small>{this.props.getRequisitionTitle(this.props.omsRequisition)}</small>
					</h2>}
					{this.state.disableInputs && this.state.orderReceived && (
						<AccessionedOrderAlert isUKOrder={isUKOrder(this.props.gearOrder)} />
					)}
				{this.props.slowFetch && 
					<div className="alert alert-warning" id="cpt-disclaimer">
                    	<p>
                        	Price calculation is taking longer than expected.
                        	Calculations can take up to a minute.  If no price is returned, please contact Genova or consult our Fee Schedule for price.
                    	</p>
                	</div>
				}
				<div className="row">
					<div className="col-md-12">
						<div className="alert alert-warning" hidden={!this.state.showError}>
							{this.state.errorMsg}
						</div>
					</div>
				</div>
				<AlertMessage type='info' display={isPrcUser && !simplifiedClientBilling}>
					The cost displayed is only as accurate as the information you provided. The final charges are determined by the services performed and by your practitioner's selections.
				</AlertMessage>
				<AlertMessage type='info' display={this.props.doNotQuotePrices}>
					Your practitioner has asked us to refer pricing questions to their office. Please contact your practitioner for payment amounts.
				</AlertMessage>
				<MedicareDiscardAlert gearOrder={this.props.gearOrder} billingMethod={this.state.form.billingMethod.value} />
				<div className="row">
					{(isPrcUser && !simplifiedClientBilling && !this.props.omsRequisition?.activationCode) && 
						<div className='col-12'>Please select the option your practitioner checked on the front of your paper requisition form or contact us at {getCustomerCareNumber(this.props.gearOrder)} for guidance on our billing options.</div>
					}
					{(isPrcUser && (simplifiedClientBilling || this.props.omsRequisition?.activationCode)) && 
						<div className='col-12'>Please select your billing method or contact us at {getCustomerCareNumber(this.props.gearOrder)} for guidance on our billing options.</div>
					}
					<div className={'col-md-6'}>
						<form id="billing-options-form" noValidate>
							<div>Select a billing option <strong className="text-danger">*</strong>
								{(!isUKOrder(this.props.gearOrder) && isPrcUser && !simplifiedClientBilling && !this.props.omsRequisition?.activationCode) &&
									<button type="button" className="btn btn-link btn-unstyled" onClick={this.showBillingOptionsImage}> <img src="/images/gear/QuestionMark_Small.jpg" alt="Question?" style={{'margin': 0, 'width': '15px', 'cursor': 'pointer', 'marginLeft': '5px'}} /></button>
								}
							</div>
						{this.state.form.billingMethod.valid === false &&
							<span className="validation-message text-danger">{this.state.form.billingMethod.message}</span>
						}
						<div className="list-group">
							{options.map(function(listValue) {
								// When there is only one sub-option, display the price at the main level and don't show sub-options
								const subOptions = listValue.subBillingOptions?.subOptions || [];
								const showSubBillingOptions = subOptions.length > 1;
								const subBillingOption = subOptions.length === 1 ? subOptions[0] : null;

								const priceLoaded = typeof this.state.requisitionPriceObject[listValue.name] !== "undefined";
								const physicianBilling = listValue.name.indexOf('Physician') !== -1;
								const skipBilling = (listValue.name === this.skipBillingOptionName || listValue.name === this.uninsuredBillingOptionName || listValue.name === this.uninsuredHRSABillingOptionName);
								const showPrice = !physicianBilling && !skipBilling && !this.props.doNotQuotePrices 
													&& !showSubBillingOptions && listValue.name !== process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE
													&& listValue.name !== process.env.REACT_APP_BILLING_ONLY_INSURANCE_VALUE;													
								const optionName = getBillingTypeDisplayName(listValue.name);
								return  (<label className={"list-group-item form-check-label " + (this.state.form.billingMethod.value === listValue.name ? "active" : "")
																							   + (this.state.form.billingMethod.valid === false ? " invalid" : "")}
											key={listValue.name}>
											<div className="row">
												<div className={showPrice ? "col-md-7" : "col-md-12"}>
													<input type="radio" className={"pull-left form-check-input " + (this.state.form.billingMethod.valid === false ? "is-invalid" : "")} 
														value={listValue.name} disabled={this.state.disableInputs} name="billingOptions" onChange={this.changeBilling} checked={this.state.form.billingMethod.value === listValue.name} />
													<div className="billing-option-text">	
														<h4 className="list-group-item-heading control-label">
															{optionName}
														</h4>
														<p className="list-group-item-text" dangerouslySetInnerHTML={{ __html: listValue.description }}/>
													</div>
												</div>
													{this.state.form.billingMethod.value === listValue.name && showSubBillingOptions &&	(
														<div className="ps-5">
														<div className="list-group-sub-item-heading">{listValue.subBillingOptions.title}</div>
														{subOptions.map((subOption) => (	
																<div className="row" key={subOption}>																																								
																<label className="list-group-item-heading control-label">															
																<div className="col-8">															
																	<input type="radio" className={"pull-left form-check-input"} 
																	value={subOption} name="subBillingOptions" onChange={this.handleSubOption} 
																	checked={this.state.form.billingMethod.subOption === subOption}/>
																	<span className="billing-option-text">{subOption}</span>
																</div>
																<div className="col-4 text-right">
																	{!priceLoaded && <span><i className='fa fa-spinner fa-spin fa-fw'></i> Calculating price...</span>}
																	{priceLoaded &&	<>{this.getPriceRange(listValue.name, subOption)}</>}
																</div>
																</label>
																</div>	
															))}														
														</div>
													)}

												{showOnlineOrderNumberOption && this.state.form.billingMethod.value === listValue.name &&
													(<div className="ps-4 col-6">
														<div><label className="list-group-sub-item-heading">Online Order Number:</label></div>
														<div className="col-7"><input className="form-control" id="online-order-number" type="text" 
														    value={listValue.onlineOrderNumber} readOnly autoComplete="off" /></div>													
													</div>)}

												{showPrice &&
													<div className="list-group-item-text col-md-5 text-right">
														{(!priceLoaded && !this.props.doNotQuotePrices) && 
															<span><i className='fa fa-spinner fa-spin fa-fw'></i> Calculating price...</span>
														}
														{(priceLoaded || this.props.doNotQuotePrices) &&
															<>
																{this.getPriceRange(listValue.name, subBillingOption)}
															</>
														}
													</div>
												}
											</div>
										</label>);
							},this)}
						</div>
						</form>
					</div>
					{(!!this.state.form.billingMethod?.value) &&
						<div className="offset-md-1 col-md-5">
							<div className="space-top"></div>
							{(!isPrcUser && this.state.form.billingMethod?.value === this.skipBillingOptionName) && (
								<div className="space-bottom">
									<p><strong>Important</strong></p>
									<p>For the best experience, we encourage all physicians to discuss pricing
										and billing options with their patients prior to completing registration.
									</p>
								</div>
							)}
							{(!this.state.covidRegistration) && (
								<PriceDescription gearOrder={this.props.gearOrder} billingOptionName={this.state.form.billingMethod?.value} requisitionPrice={this.state.requisitionPriceObject[this.state.form.billingMethod?.value]} doNotQuotePrices={this.props.doNotQuotePrices} />
							)}
						</div>
					}
				</div>
				{this.state.isPrcUser && <BillingFaqLink ukClient={isUKOrder(this.props.gearOrder)} />}
				<RequiredFieldDisclaimer />
				{!this.props.editRef && <div className="row">
					<div className="col-md-12 col-12 sticky-bottom">
					<button type="button" className="btn btn-white rounded-pill btn-sm margin-clear" onClick={this.goBack}>
						<i className="pe-2 fa fa-arrow-circle-left"></i>
						Back
					</button>
					<button type="submit" form="billing-options-form" className="btn btn-primary rounded-pill btn-sm margin-clear pull-right" onClick={this.onClick}>
						Next
						<i className="ps-2 fa fa-arrow-circle-right"></i>
					</button>
						</div>
				</div>}
				{this.state.showBillingOptionsImage && <Lightbox mainSrc="/images/gear/reg-billing-option.png" imageTitle="Genova Diagnostics Requisition Form" onCloseRequest={() => this.setState({ showBillingOptionsImage: false })} imageLoadErrorMessage="Requisition image failed to load" />}
				{/* Display the price increase confirmation modal */}
				{this.state.showConfirmPriceIncrease && (
					<Modal
						title="Please Confirm Possible Price Increase"
						confirmAction="Yes"
						cancelAction="No"
						onSave={this.confirmPriceIncrease}
						onCancel={this.denyPriceIncrease}
						modalHidden={this.closePriceIncreaseConfirmation}
					>
						<div>
							You are about to make changes to an online order which has already been confirmed by your
							patient. Depending on the billing option used, these changes may result in a higher cost of
							services.
							<br />
							<br />
							<div className="text-right">Continue with these changes?</div>
						</div>
					</Modal>
				)}
				</div>
		)
	}
}

/**
 * Gets the list of supported billing methods based on the practitioner and OMS Orderable Item
 * @return A list of OmsInputFields that represent the supported billing methods
 */
export const getSupportedBillingMethodInputFields = ({practitioner, orderableItem, userType, savedBillingMethod}) => {
	if (practitioner?.ukclient) {
		return getSupportedUKBillingMethodInputFields({practitioner, orderableItem, isPatient: userType === 'prc', savedBillingMethod});
	}

	if (!orderableItem?.inputFields?.length || !practitioner?.billingMethods?.length) {
		return [];
	}

	const inputFields = [];
	orderableItem.inputFields.forEach(field => {
		field.inputFieldParams.forEach(param => {
			if (param.name === 'Group' && param.value === 'BILLING_METHOD') {
				//respect practitioner billingRules as defined in CAT
				if(field.name === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE && practitioner.billingMethods.indexOf("Client") > -1) {
					inputFields.push(field);
				} else if (field.name === process.env.REACT_APP_BILLING_INSURANCE_VALUE && practitioner.billingMethods.indexOf("Insurance") > -1) {
					inputFields.push(field);
				} else if (field.name === process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE && practitioner.billingMethods.indexOf("Patient") > -1) {
					inputFields.push(field);
				}
			}
		});
	});	
	return inputFields;
}

const getSupportedUKBillingMethodInputFields = ({practitioner, orderableItem, isPatient, savedBillingMethod}) => {
	const billingMethods = practitioner?.billingMethods;
	const preferredBillingMethod = getDefaultUkBillingOption({ practitioner })?.billingMethod;
	const prefersClientBilling = preferredBillingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE;
	const savedClientBilling = savedBillingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE;

	if (!orderableItem?.inputFields?.length || !billingMethods?.length) {
		return [];
	}	
	const billingOptions = [];
	const onlineOrderNumber = orderableItem.inputFields.find(field => field.name === 'Web Order No')?.value?.trim() || null;
	orderableItem.inputFields.forEach(field => {
		field.inputFieldParams.forEach(param => {
			if (param.name === 'Group' && param.value === 'BILLING_METHOD') {
				// Create a copy of the field to avoid mutating the orderableItem fields
				const billingOption = { ...field };

				if (billingMethods.includes("Client") && field.name === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE) {
					// Only show the client option to patients if it is the preferred option or if it has been previously saved
					if (isPatient && !(savedClientBilling || (prefersClientBilling && !savedBillingMethod))) {
						return;
					}
					billingOption.description = 'This order will appear on your monthly statement.';
					billingOptions.push(billingOption);
				} else if (isPatient && savedClientBilling) {
					// For patients, when the saved method is client, don't show other options
					return;
				} else if (billingMethods.includes("Insurance") && field.name === process.env.REACT_APP_BILLING_ONLY_INSURANCE_VALUE) {
					// For patients, when the client prefers client-pay, only show Insurance when the saved method is Patient or Insurance
					if (isPatient && prefersClientBilling && !(savedBillingMethod || savedClientBilling)) {
						return;
					}
					billingOptions.push(billingOption);
				} else if (billingMethods.includes("Patient") && field.name === process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE) {
					// For patients, when the client has a client-pay preferred option, don't show Patient pay unless it is the saved option
					if (isPatient && prefersClientBilling && !savedBillingMethod?.startsWith(field.name)) {
						return;
					}

					billingOption.subBillingOptions = {};
					//SCR-9329: Only show CAT configured Patient Pay option to patients
					//SCR-9405: If the opposite Patient Pay option is saved, show that one instead
					// 3:Retail, 5:Retail Special, 4:Wholesale Direct
					billingOption.subBillingOptions.subOptions = [];
					if (isPatient && savedBillingMethod?.startsWith(field.name) && savedBillingMethod.includes(":")) {
						billingOption.subBillingOptions.subOptions.push(savedBillingMethod.split(":")[1]);
					} else if(practitioner?.cmsAttributes?.includes(65)) { //65: UK Student Account
						billingOption.subBillingOptions.subOptions.push('Wholesale');
					} else if (isPatient) {
						if ([3, 5].includes(practitioner?.cmsBillingTypeId)) {
							billingOption.subBillingOptions.subOptions.push('Retail');
						} else if (4 === practitioner?.cmsBillingTypeId) {
							billingOption.subBillingOptions.subOptions.push('Wholesale');
						}
					}
					if (billingOption.subBillingOptions.subOptions.length === 0) { 
						billingOption.subBillingOptions.subOptions = ['Retail', 'Wholesale'];
						billingOption.subBillingOptions.title = 'Please select Retail or Wholesale pricing.';
					}
					billingOptions.push(billingOption);	
				} else if (field.name === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE && onlineOrderNumber && onlineOrderNumber.length > 0) {
					billingOption.onlineOrderNumber = onlineOrderNumber;
					billingOptions.push(billingOption);	
				}		
			}
		});
	});

	// When an Online Order number has been saved, show that as the only option
	if (onlineOrderNumber) {
		const onlineOrderOption = billingOptions.find(option => option.name === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE);
		if (onlineOrderOption) {
			return [onlineOrderOption];
		}
	}

	// Ensure billingOptions order for UK: Client, Patient, Insurance
	const orderedNames = [
		process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE, // Client
		process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE,  // Patient
		process.env.REACT_APP_BILLING_ONLY_INSURANCE_VALUE,    // Insurance
		process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE       // Online Order
	];
	billingOptions.sort((a, b) => {
		const aIdx = orderedNames.indexOf(a.name);
		const bIdx = orderedNames.indexOf(b.name);
		return aIdx - bIdx;
	});
	return billingOptions;
}

/**
 * Determine the supported billing methods and return a list of the billing method names
 */
export const getSupportedBillingMethodNames = (practitioner, orderableItem) => {
	return getSupportedBillingMethodInputFields({practitioner, orderableItem}).map(field => field.name);
}

/**
 * Return the default billing method for a UK order.
 * Returns an object with 'billingMethod' and 'subOption' properties.
 */
export const getDefaultUkBillingOption = ({ practitioner, availableBillingOptions = [] }) => { 
	if (!practitioner) {
		return {};
	}

	let billingMethod;
	let subOption;

	//1:Deduct Credit Card, 2:Practice Monthly, 3:Retail, 5:Retail Special, 4:Wholesale Direct
	if(availableBillingOptions?.some(option => option?.name === process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE)) {
		billingMethod = process.env.REACT_APP_BILLING_ONLINE_ORDER_VALUE;
	} else if([1, 2].includes(practitioner.cmsBillingTypeId)) {
		billingMethod = process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE;
	} else if(practitioner.cmsAttributes?.includes(65)) { //65: UK Student Account)
		billingMethod = process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE;
		subOption = 'Wholesale';
	} else if([3, 5].includes(practitioner.cmsBillingTypeId)) {
		billingMethod = process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE;
		subOption = 'Retail';				
	} else if(practitioner.cmsBillingTypeId === 4) {
		billingMethod = process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE;
		subOption = 'Wholesale';
	}
	return { billingMethod, subOption };
}

// wrap the component in the workflow, requisition step, and pricing HOCs to get access to common logic
// export default withWorkflow(withRequisitionStep(withPricing(BillingOptions)));
export default withWorkflow(withRequisitionStep(withPricing(BillingOptions)));
